import { FC, useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import {
  coachSessionsEndpoints,
  ICoachExercise,
} from '../../../endpoints/coachSessions';
import styles from './ExerciseCombo.module.scss';

/**
 * Combobox con autocomplete dal catalogo esercizi + fallback testo libero.
 * Spec v1.1.1 sez 4: "selezione da library OPPURE testo libero — massima flessibilità".
 */
interface ExerciseComboProps {
  value: string;
  onChange: (name: string, exerciseId?: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const ExerciseCombo: FC<ExerciseComboProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
}) => {
  const endpoints = useEndpoints();
  const [focused, setFocused] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(value);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(value), 250);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setFocused(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  const { data } = useQuery({
    queryKey: ['exercisesSearch', debouncedSearch],
    queryFn: () =>
      endpoints
        .getExercises({
          limit: 10,
          skip: 0,
          search: debouncedSearch || undefined,
        } as any)
        .then((r: any) => r.data?.data ?? r.data ?? [])
        .catch(() => []),
    enabled: focused && debouncedSearch.length >= 2,
    staleTime: 30_000,
  });

  const suggestions: any[] = Array.isArray(data) ? data : [];

  // Libreria personale del coach (principio 3): le proprie esercitazioni già fatte
  const { data: personalData } = useQuery({
    queryKey: ['coachExerciseLibrary', debouncedSearch],
    queryFn: () =>
      coachSessionsEndpoints
        .exerciseLibrary(debouncedSearch || undefined)
        .then((r) => r.data)
        .catch(() => []),
    enabled: focused && debouncedSearch.length >= 1,
    staleTime: 30_000,
  });
  const personal: ICoachExercise[] = Array.isArray(personalData) ? personalData : [];

  // Catalogo globale senza i nomi già presenti tra le esercitazioni personali (dedup)
  const personalKeys = new Set(personal.map((p) => p.nameKey));
  const globalSuggestions = suggestions.filter(
    (ex) => !personalKeys.has(String(ex.name ?? ex.title ?? '').trim().toLowerCase()),
  );
  const showDropdown = focused && (personal.length > 0 || globalSuggestions.length > 0);

  return (
    <div ref={rootRef} className={styles.container}>
      <div className={styles.inputWrap}>
        <MagnifyingGlass size={13} className={styles.icon} />
        <input
          className={styles.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          placeholder={placeholder}
          disabled={disabled}
        />
        {value && !disabled && (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => onChange('')}
          >
            <X size={11} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className={styles.dropdown}>
          {/* Esercitazioni personali del coach (principio 3): le proprie già fatte */}
          {personal.slice(0, 6).map((p) => (
            <button
              type="button"
              key={`p_${p._id}`}
              className={styles.suggestion}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(p.name);
                setFocused(false);
              }}
            >
              <span className={styles.suggName}>{p.name}</span>
              <span className={styles.suggOwn}>tua</span>
            </button>
          ))}

          {/* Catalogo globale esercizi */}
          {globalSuggestions.slice(0, 8).map((ex) => {
            const name = ex.name ?? ex.title ?? '—';
            return (
              <button
                type="button"
                key={ex._id ?? name}
                className={styles.suggestion}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(name, ex._id);
                  setFocused(false);
                }}
              >
                <span className={styles.suggName}>{name}</span>
                {ex.category && (
                  <span className={styles.suggCat}>{ex.category}</span>
                )}
              </button>
            );
          })}
          <div className={styles.dropdownFooter}>
            Se non trovi, scrivi a mano: il testo libero è accettato.
          </div>
        </div>
      )}
    </div>
  );
};
